import jsPDF from "jspdf";
import { getCompanyData, CompanyData } from "@/components/CompanySettings";

// Corporate Design Colors
const CORPORATE_COLORS = {
  primary: [25, 65, 130] as [number, number, number],
  text: [30, 41, 59] as [number, number, number],
  textLight: [100, 116, 139] as [number, number, number],
  background: [248, 250, 252] as [number, number, number],
  border: [226, 232, 240] as [number, number, number],
  success: [34, 120, 80] as [number, number, number],
};

const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Could not load image:", e);
    return null;
  }
};

const addCompanyHeader = async (doc: jsPDF, companyData: CompanyData, logoBase64?: string | null) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25);
    } catch (e) {
      console.warn("Logo konnte nicht geladen werden:", e);
    }
  }
  
  doc.setDrawColor(...CORPORATE_COLORS.primary);
  doc.setLineWidth(0.8);
  doc.line(15, 36, pageWidth - 15, 36);
};

const addCompanyFooter = (doc: jsPDF, companyData: CompanyData) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageCount = doc.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    doc.setFillColor(...CORPORATE_COLORS.background);
    doc.rect(0, pageHeight - 35, pageWidth, 35, 'F');
    
    doc.setDrawColor(...CORPORATE_COLORS.primary);
    doc.setLineWidth(0.8);
    doc.line(15, pageHeight - 35, pageWidth - 15, pageHeight - 35);
    
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    
    let footerY = pageHeight - 28;
    doc.setTextColor(...CORPORATE_COLORS.text);
    
    if (companyData.name) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CORPORATE_COLORS.primary);
      doc.text(companyData.name, 15, footerY);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.text);
      footerY += 4;
    }
    
    const streetFull = [companyData.street, companyData.houseNumber].filter(Boolean).join(" ");
    if (streetFull) {
      doc.text(streetFull, 15, footerY);
      footerY += 4;
    }
    
    const cityFull = [companyData.postalCode, companyData.city].filter(Boolean).join(" ");
    if (cityFull) {
      doc.text(cityFull, 15, footerY);
    }
    
    // Center: HR Flash
    const now = new Date();
    const dateStr = now.toLocaleDateString("de-DE", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric" 
    });
    
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.setFont("helvetica", "bold");
    doc.text("Erstellt mit HR Flash", pageWidth / 2, pageHeight - 24, { align: "center" });
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    doc.text(dateStr, pageWidth / 2, pageHeight - 20, { align: "center" });
    
    // Right: Page number
    doc.setFontSize(9);
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.setFont("helvetica", "bold");
    doc.text(`Seite ${i} / ${pageCount}`, pageWidth - 15, pageHeight - 22, { align: "right" });
    
    doc.setTextColor(0, 0, 0);
  }
};

interface EVHomeCharging {
  id: string;
  meter_reading_kwh: number;
  meter_reading_date: string;
  meter_image_url: string | null;
  consumption_kwh: number | null;
  calculated_amount: number | null;
}

interface PreviousMeterReading {
  meter_reading_kwh: number;
  meter_reading_date: string;
}

export async function generateEVChargingSettlementPDF(
  charging: EVHomeCharging,
  employeeName: string,
  kwhPriceCents: number,
  settlementDate?: string,
  previousReading?: PreviousMeterReading | null
): Promise<void> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;

  // Header
  await addCompanyHeader(doc, companyData, logoBase64);

  let yPosition = 50;

  // Title
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Abrechnung Elektrofahrzeug-Heimladung", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 12;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  doc.text("Vergütung für privates Laden eines Dienstwagens", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 20;

  // Separator
  doc.setDrawColor(...CORPORATE_COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 15;

  // Employee info
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Mitarbeiter:", 20, yPosition);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  doc.text(employeeName, 60, yPosition);
  yPosition += 8;

  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Ablesedatum:", 20, yPosition);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  const readingDate = new Date(charging.meter_reading_date);
  doc.text(readingDate.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }), 60, yPosition);
  yPosition += 8;

  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Abrechnungsdatum:", 20, yPosition);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  const settleDate = settlementDate ? new Date(settlementDate) : new Date();
  doc.text(settleDate.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }), 75, yPosition);
  yPosition += 20;

  // Charging details box
  doc.setFillColor(...CORPORATE_COLORS.background);
  doc.setDrawColor(...CORPORATE_COLORS.primary);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, yPosition - 5, pageWidth - 30, previousReading ? 65 : 45, 3, 3, 'FD');
  yPosition += 5;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Ladedaten", 20, yPosition);
  yPosition += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  
  // Show previous meter reading if available
  if (previousReading) {
    const prevDate = new Date(previousReading.meter_reading_date);
    doc.text(`Alter Zählerstand (${prevDate.toLocaleDateString("de-DE")}):`, 25, yPosition);
    doc.text(`${previousReading.meter_reading_kwh.toFixed(1)} kWh`, 110, yPosition);
    yPosition += 7;
  }

  // Current meter reading with date
  const currentDate = new Date(charging.meter_reading_date);
  doc.text(`Neuer Zählerstand (${currentDate.toLocaleDateString("de-DE")}):`, 25, yPosition);
  doc.text(`${charging.meter_reading_kwh.toFixed(1)} kWh`, 110, yPosition);
  yPosition += 7;

  doc.text(`Verbrauch:`, 25, yPosition);
  doc.text(`${(charging.consumption_kwh || 0).toFixed(1)} kWh`, 110, yPosition);
  yPosition += 7;

  doc.text(`Strompreis:`, 25, yPosition);
  doc.text(`${(kwhPriceCents / 100).toFixed(2)} € / kWh`, 110, yPosition);
  yPosition += 20;

  // Amount box
  doc.setFillColor(...CORPORATE_COLORS.success);
  doc.roundedRect(15, yPosition - 5, pageWidth - 30, 25, 3, 3, 'F');
  yPosition += 5;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("Vergütungsbetrag:", 25, yPosition);
  doc.text(
    (charging.calculated_amount || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" }),
    pageWidth - 25,
    yPosition,
    { align: "right" }
  );
  yPosition += 30;

  // Tax notice
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.textLight);
  
  const taxText = [
    "Steuerliche Hinweise:",
    "",
    "Diese Vergütung erfolgt gemäß § 3 Nr. 50 EStG als steuer- und sozialversicherungsfreier",
    "Auslagenersatz für das Aufladen eines betrieblichen Elektrofahrzeugs an einer privaten",
    "Ladevorrichtung. Der Arbeitnehmer trägt die Stromkosten im eigenen Namen und auf eigene",
    "Rechnung. Die Erstattung erfolgt auf Basis des tatsächlich gemessenen Verbrauchs und",
    "des nachgewiesenen individuellen Strompreises.",
    "",
    "Der Zählerstand wurde durch den Mitarbeiter dokumentiert und fotografisch nachgewiesen.",
    "Diese Abrechnung dient als Beleg für die steuerliche Geltendmachung."
  ];
  
  taxText.forEach(line => {
    doc.text(line, 20, yPosition);
    yPosition += 5;
  });
  yPosition += 10;

  // Meter image if available
  if (charging.meter_image_url) {
    try {
      const meterImageBase64 = await loadImageAsBase64(charging.meter_image_url);
      if (meterImageBase64) {
        // Check if we need a new page
        if (yPosition > 200) {
          doc.addPage();
          yPosition = 30;
        }

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...CORPORATE_COLORS.primary);
        doc.text("Zählerstand-Dokumentation:", 20, yPosition);
        yPosition += 10;

        // Add image with border
        doc.setDrawColor(...CORPORATE_COLORS.border);
        doc.setLineWidth(0.5);
        const imgWidth = 80;
        const imgHeight = 60;
        doc.addImage(meterImageBase64, 'AUTO', 20, yPosition, imgWidth, imgHeight);
        doc.rect(20, yPosition, imgWidth, imgHeight, 'S');
      }
    } catch (e) {
      console.warn("Could not add meter image to PDF:", e);
    }
  }

  // Footer
  addCompanyFooter(doc, companyData);

  // Save
  const fileName = `EV_Abrechnung_${employeeName.replace(/\s+/g, "_")}_${charging.meter_reading_date}.pdf`;
  doc.save(fileName);
}
